/**
 * Pinia store holding the V2 navigation tree.
 *
 * Loaded once per session (the menu only changes when the selected company or
 * the user's rights change). Kept in a store rather than a component so a future
 * command palette can search the same list without a second fetch.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import { fetchMenu, startViewAs, stopViewAs, type MenuModule, type ViewAsState } from "@/api/menu";
import { ApiError } from "@/api/client";
import { setExpectedCompany } from "@/api/companyGuard";
import { setSsrCompany } from "@/config/menu";

export const useMenuStore = defineStore("menu", () => {
    const modules = ref<MenuModule[]>([]);
    const rights = ref<string[]>([]);
    const company = ref<{ id: number; name: string } | null>(null);
    const canSwitchCompany = ref(true);
    const viewAs = ref<ViewAsState>({ canSwitch: false, active: false, userId: null, name: null });
    const setupNeeded = ref(false);
    const setupDiag = ref("");
    const hasSubscription = ref(false);
    // True for a "Sam svoj računovodja" company (PAKET_ID = 1): it gets the ported screens listed in SSR_ROUTES.
    const isSsr = ref(false);
    // True when the user may switch between V1 and V2 (company switched on by hand, or staff).
    const canSwitchUi = ref(false);
    const loading = ref(false);
    const error   = ref<string | null>(null);
    const loaded  = ref(false);

    // The request in flight, so a second caller (router guard + sidebar) waits for the
    // answer instead of reading half-loaded state.
    let pending: Promise<void> | null = null;

    function load(force = false): Promise<void> {
        if (pending && !force) return pending;
        if (loaded.value && !force) return Promise.resolve();
        // A forced reload asks for the state as it is NOW: it must not be answered by a request that was already in
        // flight (made before the change that made the caller force it), so it waits for that one and fetches again.
        // Without this the end of the company setup read the old "setupNeeded" and sent the person back to the setup.
        const previous = pending;
        const next: Promise<void> = (previous ? previous.then(fetchOnce) : fetchOnce()).finally(() => {
            if (pending === next) pending = null;
        });
        pending = next;
        return next;
    }

    async function fetchOnce(): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const res = await fetchMenu();
            modules.value = res.modules ?? [];
            rights.value = res.rights ?? [];
            company.value = res.company ?? null;
            canSwitchCompany.value = res.canSwitchCompany !== false;
            setExpectedCompany(res.company ?? null);
            viewAs.value = res.viewAs ?? viewAs.value;
            setupNeeded.value = res.setupNeeded === true;
            setupDiag.value = res.setupDiag ?? "";
            hasSubscription.value = res.hasSubscription === true;
            isSsr.value = res.isSsr === true;
            canSwitchUi.value = res.canSwitchUi === true;
            setSsrCompany(isSsr.value);
            loaded.value = true;
        } catch (e) {
            error.value = e instanceof ApiError ? e.message : "Menija ni bilo mogoče naložiti.";
            modules.value = [];
        } finally {
            loading.value = false;
        }
    }

    /**
     * Switch the preview on/off. Rights, staff screens and the menu all change, and
     * every open view was fetched under the old identity, so a full reload is the
     * honest way to start over.
     */
    async function switchViewAs(userId: number | null): Promise<void> {
        if (userId === null) await stopViewAs();
        else await startViewAs(userId);
        window.location.assign("/v2/app/");
    }

    /** Whether the user holds a V2 screen right, e.g. can("v2_crm"). */
    function can(right: string): boolean {
        return rights.value.includes(right);
    }

    /**
     * Whether the user has a menu entry (module or leaf) with this id or action, i.e. the same
     * rights the sidebar is drawn from. A screen reached by typing its address needs one.
     */
    function mayOpen(...idsOrActions: string[]): boolean {
        const wanted = new Set(idsOrActions);
        for (const m of modules.value) {
            if (wanted.has(m.id) || wanted.has(m.action)) return true;
            for (const it of m.items) {
                if (wanted.has(it.id) || wanted.has(it.action)) return true;
            }
        }
        return false;
    }

    return { modules, rights, company, canSwitchCompany, viewAs, setupNeeded, setupDiag, hasSubscription, isSsr, canSwitchUi, loading, error, loaded, load, can, mayOpen, switchViewAs };
});
